import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { User } from '../../../domain/registrator';
import { UserService } from '../../../services/user.service';
import { MessageService } from '../../../services/message.service';
import { ConfirmDialogComponent } from '../../misc/confirm-dialog/confirm-dialog.component';
import { UserEditComponent } from '../user-edit/user-edit.component';

@Component({
  selector: 'app-users',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
  ],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss'
})
export class UsersComponent implements OnInit, AfterViewInit {
  private readonly userService = inject(UserService);
  private readonly messageService = inject(MessageService);
  private readonly dialog = inject(MatDialog);

  usersListItems: User[] = [];
  usersDataSource: MatTableDataSource<User> = new MatTableDataSource();
  pageSize: number = 10;
  pageIndex: number = 0;
  showFirstLastButtons: boolean = true;
  pageSizeOptions = [5, 10, 25, 100];
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  columnsToDisplay = ['username', 'firstname', 'lastname', 'email', 'roles', 'enabled', 'actions'];

  constructor() { }

  ngOnInit() {
    this.loadUsers();
  }

  ngAfterViewInit() {
    this.usersDataSource.paginator = this.paginator;
    this.usersDataSource.sort = this.sort;
  }

  loadUsers() {
    this.userService.getUsers().subscribe({
      next: (list) => {
        this.usersListItems = list;
        this.usersDataSource.data = this.usersListItems;
      },
      error: (error) => {
        console.log(error);
        this.messageService.showMessage('Error loading users');
      }
    });
  }

  addUser() {
    const dialogRef = this.dialog.open(UserEditComponent, {
      width: '600px',
      minWidth: '600px',
      data: {}
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.userService.createUser(result).subscribe({
          next: () => {
            this.messageService.showMessage('User created');
            this.loadUsers();
          },
          error: (error) => {
            console.log(error);
            this.messageService.showMessage('Error creating user' + (error?.error?.message?.toLowerCase() ? ': ' + error.error.message : ''));
          }
        });
      }
    });
  }

  editUser(u: User) {
    const dialogRef = this.dialog.open(UserEditComponent, {
      width: '600px',
      minWidth: '600px',
      data: { user: u }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.userService.updateUser(u.id, result).subscribe({
          next: () => {
            this.messageService.showMessage('User updated');
            this.loadUsers();
          },
          error: (error) => {
            console.log(error);
            this.messageService.showMessage('Error updating user' + (error?.error?.message?.toLowerCase() ? ': ' + error.error.message : ''));
          }
        });
      }
    });
  }

  toggleUser(u: User) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      buttonText: {
        ok: 'Yes',
        cancel: 'Cancel'
      }
    };
    dialogConfig.data.message = u.enabled
      ? 'Are you sure you want to disable this user?'
      : 'Are you sure you want to enable this user?';
    dialogConfig.height = '200px';
    dialogConfig.width = '600px';
    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.userService.toggleUser(u.id, !u.enabled).subscribe({
          next: () => {
            this.messageService.showMessage(u.enabled ? 'User disabled' : 'User enabled');
            this.loadUsers();
          },
          error: (error) => {
            console.log(error);
            this.messageService.showMessage('Error toggling user' + (error?.error?.message ? ': ' + error.error.message : ''));
          }
        });
      }
    });
  }

  deleteUser(u: User) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      message: 'Are you sure you want to delete this user?',
      buttonText: {
        ok: 'Yes',
        cancel: 'Cancel'
      }
    };
    dialogConfig.height = '200px';
    dialogConfig.width = '600px';
    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.userService.deleteUser(u.id).subscribe({
          next: () => {
            this.messageService.showMessage('User deleted');
            this.loadUsers();
          },
          error: (error) => {
            console.log(error);
            this.messageService.showMessage('Error deleting user' + (error?.error?.message ? ': ' + error.error.message : ''));
          }
        });
      }
    });
  }

  announceSortChange(sortState: Sort) {
    console.debug('sort state', sortState);
  }
}
